import type { Dimension } from '@tempo-io/sir-dto';

// The four product lenses the feed is organised by (NEX-16609, AC-F1–F6). SIR emits seven decision
// dimensions; the 7→4 grouping is a product concern and lives here, on the surface, rather than in
// the pipeline.
export const categoryLensKeys = ['risk', 'focus', 'growth', 'efficiency'] as const;
export type CategoryLensKey = (typeof categoryLensKeys)[number];

// Precedence for the primaryCategory tiebreak only — CATEGORY_LENSES' own array order below is what
// drives the cards' render order, and the two are free to differ (the design's card order is
// risk/efficiency/focus/growth; the tiebreak stays risk/focus/growth/efficiency unless asked to follow).
const PRECEDENCE: readonly CategoryLensKey[] = categoryLensKeys;

interface CategoryLens {
  key: CategoryLensKey;
  dimensions: readonly Dimension[];
  // The selected-card tint tracks the design's hexes (SEPT EAP Version E): risk #F54D42, focus #7A4FE0,
  // growth #159C6B, efficiency #D9A02B — hence violet rather than purple, green as the nearest
  // available accent to the growth hue, and amber as the nearest to the efficiency gold. `emerald`
  // would be closer still for growth but sui-theme dropped it, and a missing Tailwind class fails
  // silently (no build error, no check:tokens violation), so only names present in the theme belong
  // here. (The card icon colour lives in recommendationCategory.)
  tintClass: string;
}

// Card render order: risk, efficiency, focus, growth — matching the design's `lenses()` order
// verbatim. Each lens keeps its own established colour (keyed by `key`, not position).
export const CATEGORY_LENSES: readonly CategoryLens[] = [
  {
    key: 'risk',
    dimensions: ['risk'],
    tintClass: 'bg-bg-accent-red-subtle',
  },
  {
    key: 'efficiency',
    dimensions: ['cost', 'operational_efficiency'],
    tintClass: 'bg-bg-accent-amber-subtle',
  },
  {
    key: 'focus',
    dimensions: ['opportunity_cost'],
    tintClass: 'bg-bg-accent-violet-subtle',
  },
  {
    key: 'growth',
    dimensions: ['revenue', 'retention', 'experience'],
    tintClass: 'bg-bg-accent-green-subtle',
  },
] as const;

const LENS_BY_DIMENSION: ReadonlyMap<Dimension, CategoryLensKey> = new Map(
  CATEGORY_LENSES.flatMap((lens) => lens.dimensions.map((d) => [d, lens.key] as const)),
);

// The lens that owns a single dimension — the design's `tagColor(tag)`. Exported so a surface
// rendering RAW dimension tags (the Z7 action line, NEX-16617) can colour each one by its owning lens
// without copying the 7→4 mapping. Null for a dimension outside the four lenses.
export const lensForDimension = (dimension: Dimension): CategoryLensKey | null =>
  LENS_BY_DIMENSION.get(dimension) ?? null;

export const lensFor = (key: CategoryLensKey): CategoryLens => {
  const lens = CATEGORY_LENSES.find((candidate) => candidate.key === key);
  if (!lens) throw new Error(`Unknown category lens: ${key}`);

  return lens;
};

// AC-F1 / AC-F6 — any-tag membership: a recommendation belongs to every lens any of its dimensions
// map to, so one spanning two lenses is reachable from both.
export const matchesLens = (dimensions: readonly Dimension[], key: CategoryLensKey): boolean =>
  dimensions.some((dimension) => LENS_BY_DIMENSION.get(dimension) === key);

// AC-F6 — the card's icon and colour follow the lens holding the most of the recommendation's
// DISTINCT dimensions, ties broken by precedence. Colour only; it never affects filter membership.
//
// Returns null when nothing maps — a pre-catalog recommendation carries no dimensions, and that is
// "unknown", so it must not be coloured as though it were Risk.
export const primaryCategoryOf = (dimensions: readonly Dimension[]): CategoryLensKey | null => {
  const counts = new Map<CategoryLensKey, number>();
  for (const dimension of new Set(dimensions)) {
    const key = LENS_BY_DIMENSION.get(dimension);
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  if (counts.size === 0) return null;

  const best = Math.max(...counts.values());

  return PRECEDENCE.find((key) => counts.get(key) === best) ?? null;
};

export const filterByLens = <T extends { dimensions: readonly Dimension[] }>(
  items: readonly T[],
  key: CategoryLensKey | null,
): readonly T[] => (key === null ? items : items.filter((i) => matchesLens(i.dimensions, key)));
